import type { ColumnDef } from '@tanstack/react-table'
import type { MouseEvent } from 'react'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { cn } from '@/shared/libs/cn'
import { Skeleton } from '@/shared/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { DataTablePagination } from './data-table-pagination'

export interface PaginationConfig {
  current: number
  pageSize: number
  total: number
  onChange: (page: number, pageSize: number) => void
}

/** PaginationConfig without the onChange handler — used by list-table components */
export interface PaginationConfigInput {
  current: number
  total: number
  pageSize: number
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData>[]
  data: TData[]
  loading?: boolean
  pagination?: PaginationConfig
  emptyText?: string
  onRowClick?: (row: TData) => void
}

const LOADING_ROW_IDS = [
  'loading-row-1',
  'loading-row-2',
  'loading-row-3',
  'loading-row-4',
  'loading-row-5',
] as const

export function DataTable<TData>({
  columns,
  data,
  loading,
  pagination,
  emptyText,
  onRowClick,
}: DataTableProps<TData>) {
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: pagination ? Math.ceil(pagination.total / pagination.pageSize) : -1,
  })

  function handleRowClick(event: MouseEvent<HTMLTableRowElement>, row: TData) {
    if (!onRowClick) return
    const target = event.target as HTMLElement | null
    if (
      target?.closest('button, a, input, select, textarea, [role=button], [data-row-click-ignore]')
    )
      return
    onRowClick(row)
  }

  return (
    <div className="rounded-xl border border-zinc-200 overflow-hidden">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-zinc-50 hover:bg-zinc-50">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="text-xs font-semibold text-zinc-500 uppercase tracking-wide h-10"
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {loading ? (
            LOADING_ROW_IDS.map((rowId) => (
              <TableRow key={rowId}>
                {columns.map((column) => (
                  <TableCell key={`${rowId}-${column.id ?? String(column.header ?? 'column')}`}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn('hover:bg-zinc-50 transition-colors', onRowClick && 'cursor-pointer')}
                onClick={(event) => handleRowClick(event, row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="py-3 text-sm text-zinc-700">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center py-12 text-zinc-400 text-sm"
              >
                {emptyText ?? 'No results'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {pagination && (
        <DataTablePagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={pagination.onChange}
        />
      )}
    </div>
  )
}

export { DataTableColumnHeader } from './data-table-column-header'
export { DataTableFilterField } from './data-table-filter-field'
export { DataTablePagination } from './data-table-pagination'
export { DataTableToolbar } from './data-table-toolbar'
